import { useEffect, useMemo, useState } from "react";
import {
    bindClientTools,
    createBoundRuntimeTools,
    hasBoundPreviewTool,
    type ClientToolBindingResult,
} from "../services/clientToolBinding";
import type { WorkspaceAPI } from "../services/workspaceService";
import type { RuntimeToolDef } from "../tools/runtimeTypes";

interface ClientToolBindingState {
    api: WorkspaceAPI | null;
    result: ClientToolBindingResult;
}

interface UseClientToolsOptions {
    api: WorkspaceAPI | null;
    isReady: boolean;
    enabled: boolean;
}

interface UseClientToolsResult {
    runTimeTools: Record<string, RuntimeToolDef>;
    bindingComplete: boolean;
    hasPreviewToolBound: boolean;
}

export function useClientTools({
    api,
    isReady,
    enabled,
}: UseClientToolsOptions): UseClientToolsResult {
    const [bindingState, setBindingState] = useState<ClientToolBindingState>({
        api: null,
        result: { boundNamespaces: new Set(), novapointTools: {} },
    });

    useEffect(() => {
        if (!isReady || !api || !enabled) return;

        let cancelled = false;

        (async () => {
            const result = await bindClientTools(api);

            if (!cancelled) {
                setBindingState({ api, result });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [api, enabled, isReady]);

    const bindingComplete =
        !enabled || (!!api && bindingState.api === api);

    const runTimeTools = useMemo(() => {
        if (!enabled || bindingState.api !== api) return {};
        return createBoundRuntimeTools(bindingState.result);
    }, [api, bindingState, enabled]);

    const hasPreviewToolBound = hasBoundPreviewTool(
        bindingState.result.boundNamespaces,
    );

    return { runTimeTools, bindingComplete, hasPreviewToolBound };
}
